import styled, { css } from 'styled-components';

export const Image = styled.img`
  display: block;
  max-width: 100%;
  height: auto;
  object-fit: contain;
`;

export const Wrapper = styled.div`
  display: block;
  max-width: 100%;
  height: auto;
  object-fit: contain;
`;

export const Container = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.v3.colors.neutral[100]};
    border-radius: 0 0 16px 16px;
    padding: 32px;
    margin-bottom: 24px;

    @media (max-width: ${theme.v3.breakpoints.md}) {
      padding: 24px;
    }
  `}
`;

export const Title = styled.h2`
  ${({ theme }) => css`
    color: ${theme.v3.colors.neutral[20]};
    font-size: ${theme.v3.font.sizes['1xmedium']};
    font-weight: ${theme.v3.font.weights.medium};
    line-height: ${theme.v3.font.lineHeight.comfortable};
    margin: 0 0 32px 0;

    @media (max-width: ${theme.v3.breakpoints.md}) {
      margin: 0 0 24px 0;
    }
  `}
`;

/** Skeleton com a mesma altura do título (font-size 1xmedium) */
export const TitleSkeletonWrapper = styled.div`
  ${({ theme }) => css`
    height: ${theme.v3.font.sizes['1xmedium']};
    margin-bottom: 32px;

    @media (max-width: ${theme.v3.breakpoints.md}) {
      margin-bottom: 24px;
    }
  `}
`;

export const TitleWithToggleRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 16px;
    width: 100%;
    margin-bottom: 32px;

    @media (max-width: ${theme.v3.breakpoints.md}) {
      margin-bottom: 24px;
    }
  `}
`;

export const TitleLabel = styled.span`
  ${({ theme }) => css`
    color: ${theme.v3.colors.neutral[20]};
    font-size: ${theme.v3.font.sizes['1xmedium']};
    font-weight: ${theme.v3.font.weights.medium};
    line-height: ${theme.v3.font.lineHeight.comfortable};
    margin: 0;
  `}
`;

export const Divider = styled.div`
  ${({ theme }) => css`
    width: 1px;
    background-color: ${theme.v3.colors.neutral[80]};
    align-self: stretch;
    margin: 0;

    @media (max-width: ${theme.v3.breakpoints.md}) {
      display: none;
    }
  `}
`;

export const Adapter = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: 24px;
    align-items: flex-start;

    @media (max-width: ${theme.v3.breakpoints.lg}) {
      gap: 16px;
    }

    @media (max-width: ${theme.v3.breakpoints.md}) {
      flex-direction: column;
    }
  `}
`;

export const TitleAndColumnsContainer = styled.div`
  display: block;
  flex: 1;
  min-width: 0;
`;

export const Content = styled.div`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: auto 1px auto 1fr;
    gap: 40px;
    align-items: stretch;
    padding-left: 8px;

    @media (max-width: ${theme.v3.breakpoints.lg}) {
      gap: 24px;
      padding-left: 0;
    }

    @media (max-width: ${theme.v3.breakpoints.md}) {
      grid-template-columns: 1fr;
      gap: 24px;
      padding-left: 6px;
    }
  `}
`;

export const Column = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: 12px;
    padding: 0;
    align-items: flex-start;

    @media (max-width: ${theme.v3.breakpoints.md}) {
      padding-bottom: 24px;
      border-bottom: 1px solid ${theme.v3.colors.neutral[80]};
      
      &:has(${Divider}) {
        display: none;
      }
      
      &:last-of-type {
        border-bottom: none;
      }
    }
  `}
`;

export const ColumnIcon = styled.div`
  ${({ theme }) => css`
    color: ${theme.v3.colors.neutral[20]};
    font-size: ${theme.v3.font.sizes['1xhuge']};
    font-weight: ${theme.v3.font.weights.regular};
    display: flex;
    align-items: flex-start;
    justify-content: center;
    flex-shrink: 0;
    line-height: 1;
    
    svg {
      width: 24px;
      height: 24px;
    }
  `}
`;

export const ColumnTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  flex: 1;
`;

export const ColumnTitle = styled.h3`
  ${({ theme }) => css`
    color: ${theme.v3.colors.neutral[20]};
    font-size: ${theme.v3.font.sizes.medium};
    font-weight: ${theme.v3.font.weights.medium};
    line-height: ${theme.v3.font.lineHeight.comfortable};
    margin: 0 0 4px 0;
  `}
`;

export const ColumnDescription = styled.p`
  ${({ theme }) => css`
    color: ${theme.v3.colors.neutral[40]};
    font-size: ${theme.v3.font.sizes.mini};
    font-weight: ${theme.v3.font.weights.medium};
    line-height: ${theme.v3.font.lineHeight.comfortable};
    margin: 0 0 12px 0;
  `}
`;

export const ColumnValue = styled.div`
  ${({ theme }) => css`
    color: ${theme.v3.colors.neutral[20]};
    font-size: ${theme.v3.font.sizes['1xhuge']};
    font-weight: ${theme.v3.font.weights.regular};
    line-height: 130%;
    margin: 0;
  `}
`;

export const AlertWrapper = styled.div`
  margin-top: 24px;
`;

const getBannerStyleByType = (theme, type) => {
  switch (type) {
    case 'success':
      return css`
        background: ${theme.v3.colors.contextual.active[98]};
        color: ${theme.v3.colors.contextual.active[50]};
      `;
    case 'warning':
      return css`
        background: ${theme.v3.colors.contextual.pending[98]};
        color: ${theme.v3.colors.contextual.pending[50]};
      `;
    case 'success-gradient':
      return css`
        background: linear-gradient(90deg, ${theme.v3.colors.contextual.active[98]} 0%, ${theme.v3.colors.brand.snappyBlue[80]} 100%);
        color: ${theme.v3.colors.brand.snappyBlue[30]};
      `;
    case 'critical':
      return css`
        background: ${theme.v3.colors.contextual.problem[98]};
        color: ${theme.v3.colors.contextual.problem[50]};
      `;
    default:
      return '';
  }
};

export const StatusBanner = styled.div`
  ${({ theme, type }) => css`
    display: flex;
    align-items: center;
    margin-top: -16px;
    padding: 16px 24px;
    border-radius: 16px 16px 0 0;
    font-size: ${theme.v3.font.sizes.medium};
    line-height: ${theme.v3.font.lineHeight.comfortable};
    font-weight: ${theme.v3.font.weights.medium};
    position: relative;
    ${getBannerStyleByType(theme, type)}
  `};
`;

export const StatusIcon = styled.img`
  width: 40px;
  height: 40px;
  margin-right: 16px;
`;

export const FullProtectionBannerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-width: 0;
`;

export const AutomaticRemovalRow = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

export const ToggleLabel = styled.label`
  cursor: pointer;
  display: flex;
  align-items: center;
  margin: 0;
`;
